import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, parse, startOfMonth, endOfMonth, isValid } from 'date-fns';
import { Icon } from '@iconify/react/dist/iconify.js';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
} from 'recharts';
import { useAuth0 } from '../../../components/auth/DevAuth0Provider';
import ExportOpportunitiesCSV from '../../../components/stateseal/ExportOpportunitiesCSV';
import { useTheme } from '../../../contexts/ThemeContext';
import {
  fetchStateSnapshots,
  fetchStateSalespersons,
  fetchStateCompanies,
  fetchStateKpis,
  fetchPipelineByStage,
  fetchPipelineBySalesperson,
  fetchPipelineByCustomerType,
  fetchPipelineByCloseMonth,
  fetchStateOpportunities,
  fetchSyteLineConfig,
  asArray,
  getPipelineBaseUrl,
} from '../../../api/stateSealPipelineApi';

/** Default react-datepicker popper uses z-index: 1; portal + high z so the calendar stays above KPI cards. */
const STATE_SEAL_CLOSE_DATE_PORTAL_ID = 'state-seal-close-date-portal';

const currency = (n) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(
    Number.isFinite(Number(n)) ? Number(n) : 0
  );

const formatDisplayDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const pickNum = (...candidates) => {
  for (const v of candidates) {
    if (v !== undefined && v !== null && v !== '' && !Number.isNaN(Number(v))) return Number(v);
  }
  return 0;
};

const STAGE_ORDER = ['DISCOVERY', 'QUALIFICATION', 'ESTIMATING', 'NEGOTIATIONS', 'TESTING', 'SOURCING', 'CLOSED - WON', 'CLOSED - LOST'];

/** Per-stage funnel colors (internal chart focus only). */
const STAGE_FUNNEL_COLORS = ['#06b6d4', '#3b82f6', '#8b5cf6', '#7c3aed', '#f97316', '#22c55e', '#dc2626'];

const PROB_STACK_COLORS = { probA: '#22c55e', probB: '#eab308', probC: '#f97316' };

/** `prob` query param + DB `probability` column: single-letter bands A / B / C (not numeric %). */
const PIPELINE_PROBABILITY_BANDS = [
  { value: 'A', label: 'A' },
  { value: 'B', label: 'B' },
  { value: 'C', label: 'C' },
];

/** Map stacked bar `dataKey` / legend entry to API `prob` value. */
const probBandFromDataKey = (dataKey) => {
  if (dataKey === 'probA') return 'A';
  if (dataKey === 'probB') return 'B';
  if (dataKey === 'probC') return 'C';
  return null;
};

/** Expected-close chart X labels use `MMM yyyy` (e.g. Jan 2026) from `toCloseMonthStacked`. */
const monthChartLabelToDateRange = (label) => {
  const trimmed = String(label ?? '').trim();
  if (!trimmed) return null;
  const d = parse(trimmed, 'MMM yyyy', new Date());
  if (!isValid(d)) return null;
  return {
    dateFrom: format(startOfMonth(d), 'yyyy-MM-dd'),
    dateTo: format(endOfMonth(d), 'yyyy-MM-dd'),
  };
};

const SALESPERSON_BAR_COLORS = [
  '#3b82f6',
  '#22c55e',
  '#eab308',
  '#f97316',
  '#8b5cf6',
  '#ec4899',
  '#06b6d4',
  '#dc2626',
  '#84cc16',
  '#14b8a6',
];

const DONUT_COLORS = SALESPERSON_BAR_COLORS;

/** Recharts tooltip/legend colors for light vs dark portal theme (`ThemeContext`). */
const getChartTooltipTheme = (theme) => {
  const isDark = theme === 'dark';
  if (isDark) {
    return {
      contentStyle: {
        background: '#1e293b',
        border: '1px solid #475569',
      },
      labelStyle: { color: '#f8fafc' },
      legendLabelColor: '#e2e8f0',
    };
  }
  return {
    contentStyle: {
      background: '#ffffff',
      border: '1px solid #e2e8f0',
      boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.08)',
    },
    labelStyle: { color: '#0f172a' },
    legendLabelColor: '#475569',
  };
};

/** Legend labels use theme text; swatches use each series `entry.color` (do not set wrapper color). */
const MonthStackedBarLegend = ({ payload, legendLabelColor, onProbBandClick }) => {
  if (!payload?.length) return null;
  return (
    <ul
      className="flex flex-wrap justify-center gap-x-4 gap-y-1 pt-0.5 m-0 p-0 list-none"
      style={{ fontSize: 12 }}
    >
      {payload.map((entry, index) => {
        const band = probBandFromDataKey(entry.dataKey);
        const interactive = Boolean(band && onProbBandClick);
        return (
          <li key={entry.dataKey ?? `legend-${index}`} className="flex items-center gap-1.5">
            {interactive ? (
              <button
                type="button"
                className="flex items-center gap-1.5 rounded px-1 py-0.5 hover:bg-black/5 dark:hover:bg-white/10 cursor-pointer"
                onClick={() => onProbBandClick(band)}
              >
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-[2px]"
                  style={{ backgroundColor: entry.color }}
                  aria-hidden
                />
                <span style={{ color: legendLabelColor }}>{entry.value}</span>
              </button>
            ) : (
              <>
                <span
                  className="inline-block h-2.5 w-2.5 shrink-0 rounded-[2px]"
                  style={{ backgroundColor: entry.color }}
                  aria-hidden
                />
                <span style={{ color: legendLabelColor }}>{entry.value}</span>
              </>
            )}
          </li>
        );
      })}
    </ul>
  );
};

/** Div-based sales funnel: same shell + typography as `SalespersonTooltipContent` (cursor follows like Recharts). */
const StageFunnelTooltipContent = ({ tt, name, value, color }) => (
  <div
    className="rounded-md border text-sm shadow-md"
    style={{
      ...tt.contentStyle,
      padding: '10px 12px',
      minWidth: 120,
    }}
  >
    <div className="mb-1.5" style={tt.labelStyle}>
      {name}
    </div>
    <div className="flex items-center gap-2">
      <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: color }} aria-hidden />
      <span style={{ color, fontWeight: 600 }}>{currency(value)}</span>
    </div>
  </div>
);

/** Hover tooltip: series color on swatch + value (Recharts default loses Cell color vs labelStyle). */
const SalespersonTooltipContent = ({ active, payload, label, tt, rows }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const row = p.payload;
  const idx = Array.isArray(rows) ? rows.findIndex((r) => r.name === row?.name) : -1;
  const color =
    p.color ??
    (idx >= 0 ? SALESPERSON_BAR_COLORS[idx % SALESPERSON_BAR_COLORS.length] : undefined) ??
    '#64748b';
  const title = row?.fullName ?? row?.name ?? label ?? '—';
  return (
    <div
      className="rounded-md border text-sm shadow-md"
      style={{
        ...tt.contentStyle,
        padding: '10px 12px',
        minWidth: 120,
      }}
    >
      <div className="mb-1.5" style={tt.labelStyle}>
        {title}
      </div>
      <div className="flex items-center gap-2">
        <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: color }} aria-hidden />
        <span style={{ color, fontWeight: 600 }}>{currency(p.value)}</span>
      </div>
    </div>
  );
};

const CustomerTypePieTooltipContent = ({ active, payload, tt, rows }) => {
  if (!active || !payload?.length) return null;
  const p = payload[0];
  const sliceName = p.name ?? p.payload?.name ?? '—';
  const idx = Array.isArray(rows) ? rows.findIndex((r) => r.name === sliceName) : -1;
  const color =
    p.fill ??
    p.color ??
    (idx >= 0 ? DONUT_COLORS[idx % DONUT_COLORS.length] : undefined) ??
    '#64748b';
  return (
    <div
      className="rounded-md border text-sm shadow-md"
      style={{
        ...tt.contentStyle,
        padding: '10px 12px',
        minWidth: 120,
      }}
    >
      <div className="flex items-center gap-2 mb-1.5">
        <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: color }} aria-hidden />
        <span style={{ ...tt.labelStyle, margin: 0 }}>{sliceName}</span>
      </div>
      <div style={{ color, fontWeight: 600 }}>{currency(p.value)}</div>
    </div>
  );
};

/** Stacked month bars: month label + Prob A/B/C rows with swatches (matches rounded tooltip shell above). */
const MonthStackedBarTooltipContent = ({ active, payload, label, tt }) => {
  if (!active || !payload?.length) return null;
  return (
    <div
      className="rounded-md border text-sm shadow-md"
      style={{
        ...tt.contentStyle,
        padding: '10px 12px',
        minWidth: 160,
      }}
    >
      <p style={{ ...tt.labelStyle, margin: '0 0 8px' }}>{label}</p>
      <ul className="m-0 list-none space-y-1.5 p-0">
        {payload.map((item, i) => (
          <li key={item.dataKey ?? item.name ?? i} className="flex items-center justify-between gap-3">
            <span className="flex min-w-0 items-center gap-2">
              <span
                className="h-2.5 w-2.5 shrink-0 rounded-sm"
                style={{ backgroundColor: item.color }}
                aria-hidden
              />
              <span style={{ color: tt.legendLabelColor }}>{item.name}</span>
            </span>
            <span style={{ color: item.color, fontWeight: 600 }} className="shrink-0 tabular-nums">
              {currency(item.value)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
};

const OPPORTUNITY_TABLE_PAGE_SIZE = 25;

const PROB_SORT_ORDER = { A: 0, B: 1, C: 2 };

const toBarRows = (payload, nameKeys = ['name', 'stage', 'label', 'month', 'salesperson', 'customer_type', 'type']) => {
  const rows = asArray(payload);
  return rows.map((row) => {
    let name = '';
    for (const k of nameKeys) {
      if (row[k] !== undefined && row[k] !== null && row[k] !== '') {
        name = String(row[k]);
        break;
      }
    }
    const value = pickNum(
      row.value,
      row.amount,
      row.total,
      row.total_value,
      row.pipeline_value,
      row.sum
    );
    return { name: name || '—', value };
  });
};

/**
 * Backend `PipelineByCloseMonth`: `close_month`, `probability_a_value`, `probability_b_value`, `probability_c_value`.
 */
const toCloseMonthStacked = (payload) => {
  const rows = asArray(payload);
  return rows
    .map((row) => ({
      name: String(row.close_month ?? row.closeMonth ?? row.month ?? row.name ?? '—'),
      probA: pickNum(row.probability_a_value, row.probabilityAValue, row.prob_a_value),
      probB: pickNum(row.probability_b_value, row.probabilityBValue, row.prob_b_value),
      probC: pickNum(row.probability_c_value, row.probabilityCValue, row.prob_c_value),
    }))
    .sort((a, b) => {
      const da = parse(a.name, 'MMM yyyy', new Date());
      const db = parse(b.name, 'MMM yyyy', new Date());
      if (!isValid(da) && !isValid(db)) return 0;
      if (!isValid(da)) return 1;
      if (!isValid(db)) return -1;
      return da - db;
    });
};

const hasCloseMonthData = (rows) =>
  Array.isArray(rows) &&
  rows.some((r) => pickNum(r?.probA, r?.probB, r?.probC) > 0 || (r?.name && r.name !== '—'));

const formatSalespersonAxis = (fullName) => {
  const s = String(fullName ?? '').trim();
  if (!s) return '—';
  if (s.includes(',')) return s.split(',')[0].trim();
  const parts = s.split(/\s+/).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : s;
};

const toSalespersonHorizontal = (payload) => {
  const rows = asArray(payload)
    .map((row) => {
      let label = '';
      for (const k of ['salesperson_name', 'salesperson', 'name', 'label']) {
        if (row[k] !== undefined && row[k] !== null && row[k] !== '') {
          label = String(row[k]);
          break;
        }
      }
      const value = pickNum(row.value, row.amount, row.total, row.total_value, row.pipeline_value, row.sum);
      return {
        name: formatSalespersonAxis(label),
        value,
        fullName: label || '—',
      };
    })
    .filter((r) => r.fullName !== '—' || r.value > 0);
  rows.sort((a, b) => b.value - a.value);
  return rows.slice(0, 10);
};

const toCustomerTypeDonut = (payload) => {
  const rows = toBarRows(payload, ['name', 'customer_type', 'type', 'label']);
  rows.sort((a, b) => b.value - a.value);
  return rows.slice(0, 8);
};

const normalizeKpi = (raw) => {
  if (!raw || typeof raw !== 'object') return null;
  const bandsIn = raw.bands || raw.probability_bands;
  const hasNestedBands =
    bandsIn &&
    typeof bandsIn === 'object' &&
    (bandsIn.A != null || bandsIn.B != null || bandsIn.C != null || bandsIn.a != null || bandsIn.b != null || bandsIn.c != null);

  const bandFromNested = (key) => {
    const b = bandsIn[key] || bandsIn[key.toLowerCase()] || {};
    return {
      count: pickNum(b.count, b.opportunities),
      value: pickNum(b.value, b.amount, b.dollar_value),
      pct: pickNum(b.pct, b.percent, b.percent_of_pipeline),
    };
  };

  const won = pickNum(raw.won_opportunity_count, raw.won_count, raw.won);
  const lost = pickNum(raw.lost_opportunity_count, raw.lost_count, raw.lost);
  const open = pickNum(raw.open_opportunity_count, raw.open_count, raw.open_opportunities);
  const impliedTotal = won + lost + open;

  let bandA;
  let bandB;
  let bandC;
  if (hasNestedBands) {
    bandA = bandFromNested('A');
    bandB = bandFromNested('B');
    bandC = bandFromNested('C');
  } else {
    bandA = {
      count: pickNum(raw.probability_a_count, raw.probabilityACount),
      value: pickNum(raw.probability_a_value, raw.probabilityAValue),
      pct: pickNum(raw.probability_a_pct, raw.pct_a),
    };
    bandB = {
      count: pickNum(raw.probability_b_count, raw.probabilityBCount),
      value: pickNum(raw.probability_b_value, raw.probabilityBValue),
      pct: pickNum(raw.probability_b_pct, raw.pct_b),
    };
    bandC = {
      count: pickNum(raw.probability_c_count, raw.probabilityCCount),
      value: pickNum(raw.probability_c_value, raw.probabilityCValue),
      pct: pickNum(raw.probability_c_pct, raw.pct_c),
    };
  }

  const totalCount = pickNum(raw.total_opportunity_count, raw.total_count, raw.opportunity_count, impliedTotal);

  return {
    lastLoadedAt: raw.last_loaded_at ?? raw.lastLoadedAt,
    totalPipeline: pickNum(raw.total_pipeline_value, raw.total_pipeline, raw.pipeline_total),
    totalCount,
    openPipeline: pickNum(raw.open_pipeline_value, raw.open_pipeline),
    openCount: open,
    bandA,
    bandB,
    bandC,
    winRatePct: pickNum(raw.win_rate, raw.win_rate_pct, raw.win_pct),
    wonCount: won,
    lostCount: lost,
  };
};

const normalizeOpp = (row) => ({
  oppNumber: row.opp_number ?? row.opp_no ?? row.opportunity_number ?? row.id ?? '',
  description: row.description ?? row.desc ?? '',
  company: row.company ?? row.company_name ?? '',
  salesperson: row.salesperson_name ?? row.salesperson ?? row.sales_person ?? '',
  status: row.status ?? '',
  stage: row.stage ?? '',
  probability: row.probability ?? row.prob ?? '',
  value: pickNum(row.estimated_value, row.value, row.amount, row.total_value),
  closeDate: row.projected_close_date ?? row.close_date ?? row.closeDate ?? '',
  customerType: row.customer_type ?? row.type ?? row.customerType ?? '',
  raw: row,
});

const BAND_BADGE_CLASS = {
  A: 'bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
  B: 'bg-amber-500/20 text-amber-800 dark:text-amber-200',
  C: 'bg-orange-500/20 text-orange-800 dark:text-orange-200',
};

const ChartShell = ({ title, children, emptyHint, loading, hasData }) => (
  <div className="flex flex-col card-container rounded-md p-3 sm:px-4 sm:py-3 w-full min-h-[300px]">
    <div className="text-sm font-semibold text-font-primary mb-2 shrink-0">{title}</div>
    {!hasData ? (
      <p className="text-sm text-font-secondary flex-1">{loading ? 'Loading…' : emptyHint || 'No data.'}</p>
    ) : (
      <div className="flex-1 w-full min-h-[240px]">{children}</div>
    )}
  </div>
);

const SalesFunnelBars = ({ rows, loading, onStagePick, tooltipTheme }) => {
  const [tip, setTip] = useState(null);
  const max = useMemo(() => Math.max(1, ...rows.map((r) => r.value)), [rows]);
  const hasData = rows.length > 0;
  const canPick = Boolean(onStagePick) && !loading;
  const tt = tooltipTheme ?? getChartTooltipTheme('light');

  useEffect(() => {
    if (!hasData) setTip(null);
  }, [hasData]);

  return (
    <ChartShell
      title="Sales funnel (by stage)"
      loading={loading}
      hasData={hasData}
      emptyHint="No stage data."
    >
      <div className="space-y-2.5 pr-1 mt-4">
        {rows.map((row, i) => {
          const pct = max > 0 ? (row.value / max) * 100 : 0;
          const color = STAGE_FUNNEL_COLORS[i % STAGE_FUNNEL_COLORS.length];
          return (
            <div
              key={`${row.name}-${i}`}
              onMouseEnter={(e) => {
                setTip({
                  i,
                  clientX: e.clientX,
                  clientY: e.clientY,
                  name: row.name,
                  value: row.value,
                  color,
                });
              }}
              onMouseMove={(e) => {
                setTip((prev) =>
                  prev && prev.i === i ? { ...prev, clientX: e.clientX, clientY: e.clientY } : prev
                );
              }}
              onMouseLeave={() => {
                setTip((prev) => (prev && prev.i === i ? null : prev));
              }}
            >
              <button
                type="button"
                aria-disabled={!canPick}
                tabIndex={canPick ? 0 : -1}
                onClick={() => canPick && onStagePick(row.name)}
                className={`flex w-full items-center gap-2 min-w-0 text-left rounded-md px-0.5 py-0.5 -mx-0.5 ${
                  canPick ? 'hover:bg-black/5 dark:hover:bg-white/10 cursor-pointer' : 'cursor-default opacity-90'
                }`}
                aria-label={canPick ? `Filter by stage: ${row.name}` : row.name}
              >
                <div className="w-[30%] max-w-[140px] text-xs text-font-secondary truncate shrink-0" title={row.name}>
                  {row.name}
                </div>
                <div className="flex-1 min-w-0 h-6 rounded bg-gray-200/60 dark:bg-gray-700/60 overflow-hidden">
                  <div
                    className="h-full rounded-sm transition-[width]"
                    style={{ width: `${pct}%`, backgroundColor: color }}
                  />
                </div>
                <div className="text-xs tabular-nums text-font-primary w-[88px] text-right shrink-0">
                  {currency(row.value)}
                </div>
              </button>
            </div>
          );
        })}
      </div>
      {tip ? (
        <div
          className="fixed z-[200] pointer-events-none"
          style={{ left: tip.clientX + 12, top: tip.clientY + 12 }}
          role="tooltip"
        >
          <StageFunnelTooltipContent tt={tt} name={tip.name} value={tip.value} color={tip.color} />
        </div>
      ) : null}
    </ChartShell>
  );
};

const ExpectedCloseByMonthChart = ({ rows, loading, tooltipTheme, onMonthProbBarClick, onProbLegendClick }) => {
  const hasData = hasCloseMonthData(rows);
  const tt = tooltipTheme ?? getChartTooltipTheme('light');
  const canPick = Boolean(onMonthProbBarClick) && !loading;

  const handleStackSegmentClick = (band) => (rectData, index) => {
    if (!canPick || band == null) return;
    const payload = rectData?.payload ?? rectData;
    const monthLabel = payload?.name ?? rows[index]?.name;
    if (!monthLabel) return;
    onMonthProbBarClick({ monthLabel, band });
  };

  return (
    <ChartShell
      title="Expected close by month"
      loading={loading}
      hasData={hasData}
      emptyHint="No monthly data."
    >
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={rows} margin={{ top: 28, right: 8, left: -8, bottom: 8 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" className="opacity-40" />
          <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#9ca3af' }} interval={0} angle={-20} textAnchor="end" height={56} />
          <YAxis tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={(v) => (v >= 1e6 ? `${(v / 1e6).toFixed(1)}M` : `${Math.round(v / 1000)}k`)} />
          <Tooltip cursor={false} content={(props) => <MonthStackedBarTooltipContent {...props} tt={tt} />} />
          <Legend
            verticalAlign="bottom"
            content={(legendProps) => (
              <MonthStackedBarLegend
                {...legendProps}
                legendLabelColor={tt.legendLabelColor}
                onProbBandClick={loading ? undefined : onProbLegendClick}
              />
            )}
          />
          <Bar
            dataKey="probA"
            stackId="stack"
            name="Prob A"
            fill={PROB_STACK_COLORS.probA}
            radius={[0, 0, 0, 0]}
            cursor={canPick ? 'pointer' : 'default'}
            onClick={handleStackSegmentClick('A')}
          />
          <Bar
            dataKey="probB"
            stackId="stack"
            name="Prob B"
            fill={PROB_STACK_COLORS.probB}
            radius={[0, 0, 0, 0]}
            cursor={canPick ? 'pointer' : 'default'}
            onClick={handleStackSegmentClick('B')}
          />
          <Bar
            dataKey="probC"
            stackId="stack"
            name="Prob C"
            fill={PROB_STACK_COLORS.probC}
            radius={[2, 2, 0, 0]}
            cursor={canPick ? 'pointer' : 'default'}
            onClick={handleStackSegmentClick('C')}
          />
        </BarChart>
      </ResponsiveContainer>
    </ChartShell>
  );
};

const SalespersonHorizontalChart = ({ rows, loading, tooltipTheme, onSalespersonPick }) => {
  const hasData = rows.length > 0;
  const tt = tooltipTheme ?? getChartTooltipTheme('light');
  const canPick = Boolean(onSalespersonPick) && !loading;

  const handleBarClick = (data, index) => {
    if (!canPick) return;
    const row = rows[index] ?? data?.payload;
    const full = row?.fullName ?? row?.name;
    if (full) onSalespersonPick(full);
  };

  return (
    <ChartShell
      title="Pipeline by salesperson"
      loading={loading}
      hasData={hasData}
      emptyHint="No salesperson data."
    >
      <ResponsiveContainer width="100%" height={280}>
        <BarChart layout="vertical" data={rows} margin={{ top: 8, right: 8, left: -8, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#9ca3af" className="opacity-40" horizontal={false} />
          <XAxis type="number" tick={{ fontSize: 11, fill: '#9ca3af' }} tickFormatter={(v) => (v >= 1e6 ? `${v / 1e6}M` : `${Math.round(v / 1000)}k`)} />
          <YAxis
            type="category"
            dataKey="name"
            width={72}
            tick={{ fontSize: 11, fill: '#9ca3af' }}
            reversed
          />
          <Tooltip cursor={false} content={(props) => <SalespersonTooltipContent {...props} tt={tt} rows={rows} />} />
          <Bar
            dataKey="value"
            radius={[0, 4, 4, 0]}
            cursor={canPick ? 'pointer' : 'default'}
            onClick={handleBarClick}
          >
            {rows.map((_, i) => (
              <Cell key={`cell-sp-${i}`} fill={SALESPERSON_BAR_COLORS[i % SALESPERSON_BAR_COLORS.length]} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartShell>
  );
};

const CustomerTypeDonut = ({ rows, loading, tooltipTheme, onTypePick }) => {
  const hasData = rows.length > 0;
  const tt = tooltipTheme ?? getChartTooltipTheme('light');
  const canPick = Boolean(onTypePick) && !loading;

  const handleSliceClick = (data, index) => {
    if (!canPick) return;
    const name = data?.name ?? rows[index]?.name;
    if (name) onTypePick(name);
  };

  return (
    <ChartShell
      title="Pipeline by customer type"
      loading={loading}
      hasData={hasData}
      emptyHint="No customer type data."
    >
      <div className="flex flex-col md:flex-row md:items-center gap-4 min-h-[240px]">
        <div className="w-full md:w-[55%] h-[240px]">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={rows}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                innerRadius="50%"
                outerRadius="90%"
                paddingAngle={2}
                cursor={canPick ? 'pointer' : 'default'}
                onClick={handleSliceClick}
              >
                {rows.map((_, i) => (
                  <Cell key={`donut-${i}`} fill={DONUT_COLORS[i % DONUT_COLORS.length]} stroke="transparent" />
                ))}
              </Pie>
              <Tooltip
                cursor={false}
                content={(props) => <CustomerTypePieTooltipContent {...props} tt={tt} rows={rows} />}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <ul className="flex-1 space-y-2 text-sm min-w-0">
          {rows.map((r, i) => (
            <li key={r.name} className="flex items-center justify-between gap-2">
              <button
                type="button"
                disabled={!canPick}
                onClick={() => canPick && onTypePick(r.name)}
                className={`flex min-w-0 flex-1 items-center justify-between gap-2 rounded-md px-1 py-0.5 text-left ${
                  canPick ? 'hover:bg-black/5 dark:hover:bg-white/10 cursor-pointer' : 'cursor-default'
                }`}
                title={canPick ? `Filter by type: ${r.name}` : undefined}
              >
                <span className="flex items-center gap-2 min-w-0">
                  <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ backgroundColor: DONUT_COLORS[i % DONUT_COLORS.length] }} />
                  <span className="truncate" style={{ color: tt.legendLabelColor }} title={r.name}>
                    {r.name}
                  </span>
                </span>
                <span className="tabular-nums shrink-0 text-font-primary">{currency(r.value)}</span>
              </button>
            </li>
          ))}
        </ul>
      </div>
    </ChartShell>
  );
};

/** Top-right decorative glow for KPI row cards (matches Tailwind *-600 hues).
 *  No CSS `filter: blur()` here: the card uses `overflow-hidden`, which clips blur’s
 *  painted area outside the box, so a `blur-*` utility made the glow nearly invisible. */
const KPI_CARD_ACCENT_RADIAL = {
  blue: 'radial-gradient(circle at 100% 0%, rgb(37 99 235 / 0.35) 0%, rgb(37 99 235 / 0.08) 45%, transparent 72%)',
  green: 'radial-gradient(circle at 100% 0%, rgb(22 163 74 / 0.35) 0%, rgb(22 163 74 / 0.08) 45%, transparent 72%)',
  violet: 'radial-gradient(circle at 100% 0%, rgb(124 58 237 / 0.35) 0%, rgb(124 58 237 / 0.08) 45%, transparent 72%)',
  amber: 'radial-gradient(circle at 100% 0%, rgb(217 119 6 / 0.35) 0%, rgb(217 119 6 / 0.08) 45%, transparent 72%)',
};

/** Same hues, anchored top-left (`circle at 0% 0%`) for the wide pipeline distribution card. */
const KPI_CARD_ACCENT_RADIAL_TOP_LEFT = {
  blue: 'radial-gradient(circle at 0% 0%, rgb(37 99 235 / 0.35) 0%, rgb(37 99 235 / 0.08) 45%, transparent 72%)',
  green: 'radial-gradient(circle at 0% 0%, rgb(22 163 74 / 0.35) 0%, rgb(22 163 74 / 0.08) 45%, transparent 72%)',
  violet: 'radial-gradient(circle at 0% 0%, rgb(124 58 237 / 0.35) 0%, rgb(124 58 237 / 0.08) 45%, transparent 72%)',
  amber: 'radial-gradient(circle at 0% 0%, rgb(217 119 6 / 0.35) 0%, rgb(217 119 6 / 0.08) 45%, transparent 72%)',
};

const KpiCardTopGlow = ({ accent = 'blue', align = 'right' }) => {
  const table = align === 'left' ? KPI_CARD_ACCENT_RADIAL_TOP_LEFT : KPI_CARD_ACCENT_RADIAL;
  const fallback = align === 'left' ? KPI_CARD_ACCENT_RADIAL_TOP_LEFT.blue : KPI_CARD_ACCENT_RADIAL.blue;
  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute top-0 h-24 w-24 opacity-100 ${align === 'left' ? 'left-0' : 'right-0'}`}
      style={{ background: table[accent] ?? fallback }}
    />
  );
};

const KpiCard = ({ title, children, sub, accent = 'blue' }) => (
  <div className="bg-background border border-gray-200 dark:border-gray-700 shadow-md relative overflow-hidden rounded-md p-4 flex flex-col gap-1 justify-between min-h-[100px]">
    <KpiCardTopGlow accent={accent} />
    <div className="relative z-[1] flex flex-col gap-1 ">
      <div className="text-sm font-medium text-font-secondary">{title}</div>
      <div className="text-xl font-semibold text-font-primary">{children}</div>
      {sub && <div className="text-xs text-font-secondary mt-1">{sub}</div>}
    </div>
  </div>
);

const PipelineDistributionBands = ({ kpi, bandTotalValue, loadingMain }) => {
  const shellClass =
    'bg-background border border-gray-200 dark:border-gray-700 shadow-md relative overflow-hidden rounded-md p-4 min-h-[100px] flex flex-col justify-center';
  if (loadingMain && !kpi) {
    return (
      <div className={shellClass}>
        <KpiCardTopGlow accent="violet" />
        <div className="relative z-[1]">
          <div className="text-sm font-medium text-font-secondary mb-2">Pipeline distribution (A / B / C)</div>
          <p className="text-sm text-font-secondary">…</p>
        </div>
      </div>
    );
  }
  if (!kpi) {
    return (
      <div className={shellClass}>
        <KpiCardTopGlow accent="violet" />
        <div className="relative z-[1]">
          <div className="text-sm font-medium text-font-secondary mb-2">Pipeline distribution (A / B / C)</div>
          <p className="text-sm text-font-secondary">—</p>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-background border border-gray-200 dark:border-gray-700 shadow-md relative overflow-hidden rounded-md px-4 py-6">
      <KpiCardTopGlow accent="violet" align="left" />
      <div className="flex flex-col 2xl:flex-row items-start 2xl:items-center justify-between gap-4 relative z-[1]">
        <div className="text-base font-bold text-font-secondary whitespace-nowrap">Pipeline distribution (A / B / C)</div>
        <div className="flex flex-col 2xl:flex-row items-start 2xl:items-center flex-wrap gap-6 w-fit">
          {['A', 'B', 'C'].map((letter) => {
            const b = kpi[`band${letter}`];
            const pct = bandTotalValue > 0 && b?.value != null ? ((b.value / bandTotalValue) * 100).toFixed(1) : '0';
            return (
              <div key={letter} className="flex items-center justify-between gap-2 text-sm">
                <span className={`inline-flex items-center justify-center w-7 h-7 rounded-md text-sm font-semibold ${BAND_BADGE_CLASS[letter]}`}>
                  {letter}
                </span>
                <span className="text-font-primary font-medium tabular-nums">{currency(b?.value ?? 0)}</span>
                <span className="text-font-secondary text-sm text-right flex-1">
                  · {b?.count ?? 0} opps
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const StateSealHome = () => {
  const { getAccessTokenSilently } = useAuth0();
  const auth = { getAccessTokenSilently };
  const themeContext = useTheme();
  const chartTooltipTheme = useMemo(
    () => getChartTooltipTheme(themeContext?.theme ?? 'light'),
    [themeContext?.theme]
  );

  const [snapshots, setSnapshots] = useState([]);
  const [snapshotDate, setSnapshotDate] = useState('');
  const [salespersonOptions, setSalespersonOptions] = useState([]);
  const [salesperson, setSalesperson] = useState([]);
  const [companyOptions, setCompanyOptions] = useState([]);
  const [filterCompany, setFilterCompany] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const [kpiPayload, setKpiPayload] = useState(null);
  const [winRatePayload, setWinRatePayload] = useState(null);
  const [stageRows, setStageRows] = useState([]);
  const [closeMonthStacked, setCloseMonthStacked] = useState([]);
  const [salespersonBars, setSalespersonBars] = useState([]);
  const [customerTypeDonut, setCustomerTypeDonut] = useState([]);
  const [opportunities, setOpportunities] = useState([]);

  const [syteLineBase, setSyteLineBase] = useState('');

  const [loadingSnapshots, setLoadingSnapshots] = useState(true);
  const [loadingMain, setLoadingMain] = useState(false);
  const [loadingSalespeople, setLoadingSalespeople] = useState(false);
  const [error, setError] = useState(null);

  const [tableSearch, setTableSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState([]);
  const [filterStage, setFilterStage] = useState([]);
  const [filterProb, setFilterProb] = useState([]);
  const [filterType, setFilterType] = useState([]);
  const [oppTablePage, setOppTablePage] = useState(1);

  // Accumulated option lists — union across all loads so selected filters don't erase other options
  const [statusOptions, setStatusOptions] = useState([]);
  const [stageOptions, setStageOptions] = useState([]);
  const [customerTypeOptions, setCustomerTypeOptions] = useState([]);
  const [animationStates, setAnimationStates] = useState({ header: false });
  const stageDefaultApplied = useRef(false);

  useEffect(() => {
    if (!stageDefaultApplied.current && stageOptions.length > 0) {
      stageDefaultApplied.current = true;
      setFilterStage(stageOptions.filter((s) => s !== 'CLOSED - WON' && s !== 'CLOSED - LOST'));
    }
  }, [stageOptions]);

  const [showSalespersonDropdown, setShowSalespersonDropdown] = useState(false);
  const [showStatusDropdown, setShowStatusDropdown] = useState(false);
  const [showStageDropdown, setShowStageDropdown] = useState(false);
  const [showProbDropdown, setShowProbDropdown] = useState(false);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [showTypeDropdown, setShowTypeDropdown] = useState(false);

  /** Passed to every pipeline GET (KPIs, charts, opportunities) — must stay in sync with global filter UI. */
  const queryFilters = useMemo(
    () => ({
      snapshotDate: snapshotDate || undefined,
      salesperson: salesperson.length > 0 ? salesperson : undefined,
      company: filterCompany.length > 0 ? filterCompany : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      status: filterStatus.length > 0 ? filterStatus : undefined,
      stage: filterStage.length > 0 ? filterStage : undefined,
      prob: filterProb.length > 0 ? filterProb : undefined,
      type: filterType.length > 0 ? filterType : undefined,
    }),
    [snapshotDate, salesperson, filterCompany, dateFrom, dateTo, filterStatus, filterStage, filterProb, filterType]
  );

  /** Win rate is always calculated across all stages/statuses — only respects the other filters. */
  const winRateFilters = useMemo(
    () => ({
      snapshotDate: snapshotDate || undefined,
      salesperson: salesperson.length > 0 ? salesperson : undefined,
      company: filterCompany.length > 0 ? filterCompany : undefined,
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      prob: filterProb.length > 0 ? filterProb : undefined,
      type: filterType.length > 0 ? filterType : undefined,
    }),
    [snapshotDate, salesperson, filterCompany, dateFrom, dateTo, filterProb, filterType]
  );

  const isHistorical = Boolean(snapshotDate);
  const kpi = useMemo(() => normalizeKpi(kpiPayload), [kpiPayload]);
  const winRateKpi = useMemo(() => normalizeKpi(winRatePayload), [winRatePayload]);

  const sortedStageRows = useMemo(() => {
    const normalize = (s) => s.trim().toUpperCase();
    return stageRows.slice().sort((a, b) => {
      const ai = STAGE_ORDER.indexOf(normalize(a.name));
      const bi = STAGE_ORDER.indexOf(normalize(b.name));
      if (ai === -1 && bi === -1) return a.name.localeCompare(b.name);
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
  }, [stageRows]);

  const bandTotalValue = useMemo(() => {
    if (!kpi) return 0;
    return kpi.bandA.value + kpi.bandB.value + kpi.bandC.value;
  }, [kpi]);

  useEffect(() => {
    const t = setTimeout(() => setAnimationStates((prev) => ({ ...prev, header: true })), 100);
    return () => clearTimeout(t);
  }, []);

  const loadSnapshotsAndConfig = useCallback(async () => {
    setError(null);
    if (!getPipelineBaseUrl()) {
      setLoadingSnapshots(false);
      setError({ message: 'Set API URL to your pipeline API base (…/api/v1).' });
      return;
    }
    setLoadingSnapshots(true);
    try {
      const [snapRes, syteline] = await Promise.all([
        fetchStateSnapshots(auth),
        fetchSyteLineConfig(auth).catch(() => null),
      ]);
      const list = asArray(snapRes);
      setSnapshots(list);
      const base = syteline?.base_url ?? syteline?.baseUrl ?? '';
      setSyteLineBase(typeof base === 'string' ? base.replace(/\/$/, '') : '');
    } catch (e) {
      setError({ message: e.message || 'Failed to load snapshots.', status: e.status });
    } finally {
      setLoadingSnapshots(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    loadSnapshotsAndConfig();
  }, [loadSnapshotsAndConfig]);

  useEffect(() => {
    let cancelled = false;
    async function loadSp() {
      if (!getPipelineBaseUrl()) return;
      setLoadingSalespeople(true);
      try {
        const spFilters = { snapshotDate: snapshotDate || undefined };
        const res = await fetchStateSalespersons(auth, spFilters);
        const arr = asArray(res);
        const names = arr
          .map((x) => (typeof x === 'string' ? x : x.name ?? x.salesperson ?? x.label))
          .filter(Boolean);
        if (!cancelled) setSalespersonOptions(names);
      } catch {
        if (!cancelled) setSalespersonOptions([]);
      } finally {
        if (!cancelled) setLoadingSalespeople(false);
      }
    }
    loadSp();
    return () => {
      cancelled = true;
    };
  }, [snapshotDate, getAccessTokenSilently]);

  useEffect(() => {
    let cancelled = false;
    async function loadCompanies() {
      if (!getPipelineBaseUrl()) return;
      setLoadingCompanies(true);
      try {
        const res = await fetchStateCompanies(auth, { snapshotDate: snapshotDate || undefined });
        const arr = asArray(res);
        const names = arr.map((x) => (typeof x === 'string' ? x : x.name ?? x.company ?? x.label)).filter(Boolean);
        if (!cancelled) setCompanyOptions(names);
      } catch {
        if (!cancelled) setCompanyOptions([]);
      } finally {
        if (!cancelled) setLoadingCompanies(false);
      }
    }
    loadCompanies();
    return () => {
      cancelled = true;
    };
  }, [snapshotDate, getAccessTokenSilently]);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!getPipelineBaseUrl()) return;
      setLoadingMain(true);
      setError(null);
      try {
        const [kpis, winKpis, st, sm, sp, ct, op] = await Promise.all([
          fetchStateKpis(auth, queryFilters),
          fetchStateKpis(auth, winRateFilters),
          fetchPipelineByStage(auth, queryFilters),
          fetchPipelineByCloseMonth(auth, queryFilters),
          fetchPipelineBySalesperson(auth, queryFilters),
          fetchPipelineByCustomerType(auth, queryFilters),
          fetchStateOpportunities(auth, queryFilters, { limit: 500, offset: 0, search: '' }),
        ]);
        if (cancelled) return;
        setKpiPayload(kpis && typeof kpis === 'object' ? kpis : null);
        setWinRatePayload(winKpis && typeof winKpis === 'object' ? winKpis : null);
        setStageRows(toBarRows(st));
        setCloseMonthStacked(toCloseMonthStacked(sm));
        setSalespersonBars(toSalespersonHorizontal(sp));
        setCustomerTypeDonut(toCustomerTypeDonut(ct));
        const opRows = asArray(op?.items ?? op?.data ?? op?.opportunities ?? op);
        const normalized = opRows.map(normalizeOpp);
        setOpportunities(normalized);
        // Accumulate filter options — union with existing so applying a filter doesn't erase other options
        const accum = (key) => (prev) => {
          const next = new Set(prev);
          normalized.forEach((o) => { if (o[key] != null && String(o[key]).trim()) next.add(String(o[key])); });
          return Array.from(next).sort();
        };
        setStatusOptions(accum('status'));
        setStageOptions(accum('stage'));
        setCustomerTypeOptions(accum('customerType'));
      } catch (e) {
        if (!cancelled) setError({ message: e.message || 'Failed to load pipeline data.', status: e.status });
      } finally {
        if (!cancelled) setLoadingMain(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [queryFilters, getAccessTokenSilently]);

  const clearFilters = () => {
    setSalesperson([]);
    setFilterCompany([]);
    setDateFrom('');
    setDateTo('');
    setFilterStatus([]);
    setFilterStage([]);
    setFilterProb([]);
    setFilterType([]);
  };

  const hasGlobalFilters = Boolean(
    salesperson.length > 0 || filterCompany.length > 0 || dateFrom || dateTo || filterStatus.length > 0 || filterStage.length > 0 || filterProb.length > 0 || filterType.length > 0
  );

  const handleChartStagePick = useCallback((stage) => {
    if (!stage) return;
    setFilterStage([String(stage)]);
  }, []);

  /** Stacked bar segment: month range + probability band (A/B/C). */
  const handleChartMonthProbBarClick = useCallback(({ monthLabel, band }) => {
    const range = monthChartLabelToDateRange(monthLabel);
    if (range) {
      setDateFrom(range.dateFrom);
      setDateTo(range.dateTo);
    }
    if (band) setFilterProb([band]);
  }, []);

  /** Legend only: set probability band without changing dates. */
  const handleChartProbLegendClick = useCallback((band) => {
    if (band) setFilterProb([band]);
  }, []);

  const handleChartSalespersonPick = useCallback((fullName) => {
    if (!fullName) return;
    setSalesperson([String(fullName)]);
  }, []);

  const handleChartTypePick = useCallback((typeName) => {
    if (!typeName) return;
    setFilterType([String(typeName)]);
  }, []);

  const uniqueForColumn = (key) => {
    const set = new Set();
    opportunities.forEach((o) => {
      const v = o[key];
      if (v !== undefined && v !== null && String(v).trim() !== '') set.add(String(v));
    });
    return Array.from(set).sort();
  };

  const toggleMultiFilter = (value, currentArr, setArr) => {
    setArr(currentArr.includes(value) ? currentArr.filter((v) => v !== value) : [...currentArr, value]);
  };

  /** Server applies snapshot/salesperson/dates/status/stage/prob/type; client only narrows by search then sorts. */
  const filteredOpportunities = useMemo(() => {
    const q = tableSearch.trim().toLowerCase();
    const filtered = !q
      ? opportunities
      : opportunities.filter((o) => {
          const hay = [String(o.oppNumber), o.description, o.company, o.salesperson]
            .join(' ')
            .toLowerCase();
          return hay.includes(q);
        });

    return [...filtered].sort((a, b) => {
      // Primary: probability band A → B → C; unknown bands sink last
      const pa = PROB_SORT_ORDER[a.probability] ?? 3;
      const pb = PROB_SORT_ORDER[b.probability] ?? 3;
      if (pa !== pb) return pa - pb;
      // Secondary: close date ascending within each band; rows with no date sink to the bottom
      const da = a.closeDate ? new Date(a.closeDate).getTime() : Infinity;
      const db = b.closeDate ? new Date(b.closeDate).getTime() : Infinity;
      return da - db;
    });
  }, [opportunities, tableSearch]);

  useEffect(() => {
    setOppTablePage(1);
  }, [tableSearch, queryFilters]);

  const oppPagination = useMemo(() => {
    const total = filteredOpportunities.length;
    const totalPages = Math.max(1, Math.ceil(total / OPPORTUNITY_TABLE_PAGE_SIZE));
    const startIndex = (oppTablePage - 1) * OPPORTUNITY_TABLE_PAGE_SIZE;
    const rows = filteredOpportunities.slice(startIndex, startIndex + OPPORTUNITY_TABLE_PAGE_SIZE);
    return { total, totalPages, startIndex, rows };
  }, [filteredOpportunities, oppTablePage]);

  useEffect(() => {
    const tp = Math.max(1, Math.ceil(filteredOpportunities.length / OPPORTUNITY_TABLE_PAGE_SIZE));
    setOppTablePage((p) => Math.min(p, tp));
  }, [filteredOpportunities.length]);

  const handleOppClick = async (oppNo) => {
    if (!syteLineBase || !oppNo) return;
    try {
      await navigator.clipboard.writeText(String(oppNo));
    } catch {
      /* ignore */
    }
    const url = `${syteLineBase}/WSWebForm.aspx?Form=Opportunities`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const snapshotSelectOptions = useMemo(() => {
    const opts = [{ value: '', label: 'Current', count: null }];
    snapshots.forEach((s) => {
      const d = s.snapshot_date ?? s.snapshotDate ?? s.date;
      const c = s.record_count ?? s.recordCount;
      const label = d ? `${formatDisplayDate(d)} (${c ?? '—'} records)` : `Snapshot (${c ?? '—'})`;
      opts.push({ value: d ? String(d) : '', label, count: c });
    });
    return opts;
  }, [snapshots]);

  return (
    <div className="w-full max-w-[1600px] mx-auto px-3 sm:px-4 py-4 space-y-6">
      <div
        className={`dashboard-header-responsive transition-all duration-700 ease-out ${
          animationStates.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}
      >
        <div className="flex flex-col gap-2 items-start w-full">
          <h1 className="header-textbox flex-wrap gap-2">
            <Icon icon="mdi:view-dashboard" className="header-icon" aria-hidden />
            <span className="text-font-primary">Opportunity Pipeline</span>
            {isHistorical && (
              <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-700 dark:text-amber-300">
                Viewing historical data
              </span>
            )}
          </h1>
          <p className="text-font-secondary text-sm sm:text-base">
            {isHistorical
              ? `Viewing historical snapshot from ${formatDisplayDate(snapshotDate)}`
              : 'Real-time view of opportunities, pipeline value, and win rate.'}
          </p>
        </div>
        <div
          className={`flex-shrink-0 flex flex-wrap items-center gap-3 transition-all duration-500 ease-out ${
            animationStates.header ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
        >
          <label className="flex items-center gap-2 text-sm text-font-secondary">
            <span>Snapshot</span>
            <select
              className="button-container rounded-md px-2 py-1.5 text-sm text-font-primary border border-gray-200 dark:border-gray-600 bg-transparent min-w-[200px]"
              value={snapshotDate}
              onChange={(e) => setSnapshotDate(e.target.value)}
              disabled={loadingSnapshots}
            >
              {snapshotSelectOptions.map((o) => (
                <option key={o.label + o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <div className="text-sm text-font-secondary">
            Last updated:{' '}
            <span className="text-font-primary font-medium">
              {kpi?.lastLoadedAt ? formatDisplayDate(kpi.lastLoadedAt) : loadingMain ? '…' : '—'}
            </span>
          </div>
        </div>
      </div>

      {error && (
        <div
          className="rounded-md border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm text-red-200"
          role="alert"
        >
          {error.status && <span className="font-medium">{error.status} — </span>}
          {error.message}
        </div>
      )}

      {/* Active filters */}
      {(salesperson.length > 0 || filterStatus.length > 0 || filterStage.length > 0 || filterProb.length > 0 || filterCompany.length > 0 || filterType.length > 0) && (
        <div className="flex flex-col gap-1">
          <div className="flex items-start justify-between">
            <span className="text-primary font-semibold text-lg">Active filters</span>
            <button
              type="button"
              className="flex items-center gap-1 text-sm px-3 py-2 h-fit rounded-md font-semibold button-container text-font-secondary"
              onClick={clearFilters}
            >
              <Icon icon="material-symbols:close" className="text-lg text-error" />
              Clear filters
            </button>
          </div>
          <div className="flex flex-wrap gap-4">
            {[
              { label: 'Salesperson', values: salesperson, setter: setSalesperson },
              { label: 'Status', values: filterStatus, setter: setFilterStatus },
              { label: 'Stage', values: filterStage, setter: setFilterStage },
              { label: 'Probability', values: filterProb, setter: setFilterProb },
              { label: 'Client', values: filterCompany, setter: setFilterCompany },
              { label: 'Customer type', values: filterType, setter: setFilterType },
            ].filter(({ values }) => values.length > 0).map(({ label, values, setter }) => (
              <div key={label} className="flex flex-col gap-2">
                <span className="text-font-secondary text-sm">{label}</span>
                <div className="flex flex-wrap gap-1">
                  {values.map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => toggleMultiFilter(v, values, setter)}
                      className="flex items-center gap-1 bg-primary-20 font-bold text-font-primary text-sm px-2 py-1.5 rounded hover:opacity-70 transition-opacity"
                    >
                      {v}
                      <Icon icon="material-symbols:close" className="text-base text-error" />
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Global filters */}
      <div className="flex flex-wrap items-start gap-4">
        {/* Salesperson multi-select */}
        <div className="flex-1 flex flex-col gap-1 text-sm relative">
          <span className="text-font-secondary">Salesperson</span>
          <div
            role="button"
            tabIndex={loadingSalespeople ? -1 : 0}
            onClick={() => !loadingSalespeople && setShowSalespersonDropdown(!showSalespersonDropdown)}
            className={`flex items-center justify-between gap-2 px-2 py-3 rounded-md button-container text-sm text-font-primary min-w-[180px] select-none ${loadingSalespeople ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className="text-font-secondary font-semibold">{loadingSalespeople ? 'Loading…' : 'Salesperson'}</span>
            <Icon icon={showSalespersonDropdown ? 'material-symbols:keyboard-arrow-up' : 'material-symbols:keyboard-arrow-down'} className="text-lg text-accent flex-shrink-0" />
          </div>
          {showSalespersonDropdown && (
            <>
              <div className="fixed inset-0 z-[199]" onClick={() => setShowSalespersonDropdown(false)} />
              <div className="absolute top-full left-0 mt-1 bg-background shadow-light rounded-md border border-secondary-50 z-[200] min-w-[180px] max-h-60 overflow-y-auto scrollable">
                {salespersonOptions.map((name) => {
                  const selected = salesperson.includes(name);
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => toggleMultiFilter(name, salesperson, setSalesperson)}
                      className={`w-full text-left px-2 py-1.5 hover:bg-bgSecondary transition-colors flex items-center justify-between text-sm ${selected ? 'text-primary' : 'text-font-secondary'}`}
                    >
                      <span>{name}</span>
                      {selected && <Icon icon="material-symbols:check" className="text-base flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Status multi-select */}
        <div className="flex-1 flex flex-col gap-1 text-sm relative">
          <span className="text-font-secondary">Status</span>
          <div
            role="button"
            tabIndex={loadingMain ? -1 : 0}
            onClick={() => !loadingMain && setShowStatusDropdown(!showStatusDropdown)}
            className={`flex items-center justify-between gap-2 px-2 py-3 rounded-md button-container text-sm text-font-primary min-w-[180px] select-none ${loadingMain ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className="text-font-secondary font-semibold">Status</span>
            <Icon icon={showStatusDropdown ? 'material-symbols:keyboard-arrow-up' : 'material-symbols:keyboard-arrow-down'} className="text-lg text-accent flex-shrink-0" />
          </div>
          {showStatusDropdown && (
            <>
              <div className="fixed inset-0 z-[199]" onClick={() => setShowStatusDropdown(false)} />
              <div className="absolute top-full left-0 mt-1 bg-background shadow-light rounded-md border border-secondary-50 z-[200] min-w-[180px] max-h-60 overflow-y-auto scrollable">
                {statusOptions.map((x) => {
                  const selected = filterStatus.includes(x);
                  return (
                    <button
                      key={x}
                      type="button"
                      onClick={() => toggleMultiFilter(x, filterStatus, setFilterStatus)}
                      className={`w-full text-left px-2 py-1.5 hover:bg-bgSecondary transition-colors flex items-center justify-between text-sm ${selected ? 'text-primary' : 'text-font-secondary'}`}
                    >
                      <span>{x}</span>
                      {selected && <Icon icon="material-symbols:check" className="text-base flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Stage multi-select */}
        <div className="flex-1 flex flex-col gap-1 text-sm relative">
          <span className="text-font-secondary">Stage</span>
          <div
            role="button"
            tabIndex={loadingMain ? -1 : 0}
            onClick={() => !loadingMain && setShowStageDropdown(!showStageDropdown)}
            className={`flex items-center justify-between gap-2 px-2 py-3 rounded-md button-container text-sm text-font-primary min-w-[180px] select-none ${loadingMain ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className="text-font-secondary font-semibold">Stage</span>
            <Icon icon={showStageDropdown ? 'material-symbols:keyboard-arrow-up' : 'material-symbols:keyboard-arrow-down'} className="text-lg text-accent flex-shrink-0" />
          </div>
          {showStageDropdown && (
            <>
              <div className="fixed inset-0 z-[199]" onClick={() => setShowStageDropdown(false)} />
              <div className="absolute top-full left-0 mt-1 bg-background shadow-light rounded-md border border-secondary-50 z-[200] min-w-[180px] max-h-60 overflow-y-auto scrollable">
                {(() => {
                  // CLOSED-DISQUALIFIED will be added to STAGE_ORDER once backend supports it.
                  return stageOptions.slice()
                    .slice()
                    .sort((a, b) => {
                      const normalize = (s) => s.trim().toUpperCase();
                      const ai = STAGE_ORDER.indexOf(normalize(a));
                      const bi = STAGE_ORDER.indexOf(normalize(b));
                      if (ai === -1 && bi === -1) return a.localeCompare(b);
                      if (ai === -1) return 1;
                      if (bi === -1) return -1;
                      return ai - bi;
                    })
                    .map((x) => {
                      const selected = filterStage.includes(x);
                      return (
                        <button
                          key={x}
                          type="button"
                          onClick={() => toggleMultiFilter(x, filterStage, setFilterStage)}
                          className={`w-full text-left px-2 py-1.5 hover:bg-bgSecondary transition-colors flex items-center justify-between text-sm ${selected ? 'text-primary' : 'text-font-secondary'}`}
                        >
                          <span>{x}</span>
                          {selected && <Icon icon="material-symbols:check" className="text-base flex-shrink-0" />}
                        </button>
                      );
                    });
                })()}
              </div>
            </>
          )}
        </div>

        {/* Probability multi-select */}
        <div className="flex-1 flex flex-col gap-1 text-sm relative">
          <span className="text-font-secondary">Probability (band)</span>
          <div
            role="button"
            tabIndex={loadingMain ? -1 : 0}
            onClick={() => !loadingMain && setShowProbDropdown(!showProbDropdown)}
            title="A, B, or C as stored on opportunities (pipeline API prob filter)."
            className={`flex items-center justify-between gap-2 px-2 py-3 rounded-md button-container text-sm text-font-primary min-w-[180px] select-none ${loadingMain ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className="text-font-secondary font-semibold">Probability</span>
            <Icon icon={showProbDropdown ? 'material-symbols:keyboard-arrow-up' : 'material-symbols:keyboard-arrow-down'} className="text-lg text-accent flex-shrink-0" />
          </div>
          {showProbDropdown && (
            <>
              <div className="fixed inset-0 z-[199]" onClick={() => setShowProbDropdown(false)} />
              <div className="absolute top-full left-0 mt-1 bg-background shadow-light rounded-md border border-secondary-50 z-[200] min-w-[180px] max-h-60 overflow-y-auto scrollable">
                {PIPELINE_PROBABILITY_BANDS.map(({ value, label }) => {
                  const selected = filterProb.includes(value);
                  return (
                    <button
                      key={value}
                      type="button"
                      onClick={() => toggleMultiFilter(value, filterProb, setFilterProb)}
                      className={`w-full text-left px-2 py-1.5 hover:bg-bgSecondary transition-colors flex items-center justify-between text-sm ${selected ? 'text-primary' : 'text-font-secondary'}`}
                    >
                      <span>{label}</span>
                      {selected && <Icon icon="material-symbols:check" className="text-base flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Client (company) multi-select */}
        <div className="flex-1 flex flex-col gap-1 text-sm relative">
          <span className="text-font-secondary">Client</span>
          <div
            role="button"
            tabIndex={loadingCompanies ? -1 : 0}
            onClick={() => !loadingCompanies && setShowCompanyDropdown(!showCompanyDropdown)}
            className={`flex items-center justify-between gap-2 px-2 py-3 rounded-md button-container text-sm text-font-primary min-w-[180px] select-none ${loadingCompanies ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className="text-font-secondary font-semibold">{loadingCompanies ? 'Loading…' : 'Client'}</span>
            <Icon icon={showCompanyDropdown ? 'material-symbols:keyboard-arrow-up' : 'material-symbols:keyboard-arrow-down'} className="text-lg text-accent flex-shrink-0" />
          </div>
          {showCompanyDropdown && (
            <>
              <div className="fixed inset-0 z-[199]" onClick={() => setShowCompanyDropdown(false)} />
              <div className="absolute top-full left-0 mt-1 bg-background shadow-light rounded-md border border-secondary-50 z-[200] min-w-[180px] max-h-60 overflow-y-auto scrollable">
                {companyOptions.map((name) => {
                  const selected = filterCompany.includes(name);
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => toggleMultiFilter(name, filterCompany, setFilterCompany)}
                      className={`w-full text-left px-2 py-1.5 hover:bg-bgSecondary transition-colors flex items-center justify-between text-sm ${selected ? 'text-primary' : 'text-font-secondary'}`}
                    >
                      <span>{name}</span>
                      {selected && <Icon icon="material-symbols:check" className="text-base flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Customer type multi-select */}
        <div className="flex-1 flex flex-col gap-1 text-sm relative">
          <span className="text-font-secondary font-semibold">Customer type</span>
          <div
            role="button"
            tabIndex={loadingMain ? -1 : 0}
            onClick={() => !loadingMain && setShowTypeDropdown(!showTypeDropdown)}
            className={`flex items-center justify-between gap-2 px-2 py-3 rounded-md button-container text-sm text-font-primary min-w-[180px] select-none ${loadingMain ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
          >
            <span className="text-font-secondary font-semibold">Customer type</span>
            <Icon icon={showTypeDropdown ? 'material-symbols:keyboard-arrow-up' : 'material-symbols:keyboard-arrow-down'} className="text-lg text-accent flex-shrink-0" />
          </div>
          {showTypeDropdown && (
            <>
              <div className="fixed inset-0 z-[199]" onClick={() => setShowTypeDropdown(false)} />
              <div className="absolute top-full left-0 mt-1 bg-background shadow-light rounded-md border border-secondary-50 z-[200] min-w-[180px] max-h-60 overflow-y-auto scrollable">
                {customerTypeOptions.map((x) => {
                  const selected = filterType.includes(x);
                  return (
                    <button
                      key={x}
                      type="button"
                      onClick={() => toggleMultiFilter(x, filterType, setFilterType)}
                      className={`w-full text-left px-2 py-1.5 hover:bg-bgSecondary transition-colors flex items-center justify-between text-sm ${selected ? 'text-primary' : 'text-font-secondary'}`}
                    >
                      <span>{x}</span>
                      {selected && <Icon icon="material-symbols:check" className="text-base flex-shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
      <div className="flex flex-row gap-4 items-center">
        <label className="flex-1 flex flex-col gap-1 text-sm">
          <span className="text-font-secondary font-semibold">Close date from</span>
          <DatePicker
            selected={dateFrom ? parse(dateFrom, 'yyyy-MM-dd', new Date()) : null}
            onChange={(d) => setDateFrom(d ? format(d, 'yyyy-MM-dd') : '')}
            dateFormat="MM/dd/yyyy"
            placeholderText="mm/dd/yyyy"
            portalId={STATE_SEAL_CLOSE_DATE_PORTAL_ID}
            popperClassName="!z-[9999]"
            calendarClassName="rounded-md"
            className="rounded-md px-2 py-2 text-sm text-font-primary border border-gray-200 dark:border-gray-600 bg-transparent w-full min-w-[160px]"
            wrapperClassName="w-full"
          />
        </label>
        <label className="flex-1 flex flex-col gap-1 text-sm">
          <span className="text-font-secondary font-semibold">Close date to</span>
          <DatePicker
            selected={dateTo ? parse(dateTo, 'yyyy-MM-dd', new Date()) : null}
            onChange={(d) => setDateTo(d ? format(d, 'yyyy-MM-dd') : '')}
            dateFormat="MM/dd/yyyy"
            placeholderText="mm/dd/yyyy"
            portalId={STATE_SEAL_CLOSE_DATE_PORTAL_ID}
            popperClassName="!z-[9999]"
            calendarClassName="rounded-md"
            className="rounded-md px-2 py-2 text-sm text-font-primary border border-gray-200 dark:border-gray-600 bg-transparent w-full min-w-[160px]"
            wrapperClassName="w-full"
          />
        </label>
      </div>

      {/* KPI row */}
      <PipelineDistributionBands kpi={kpi} bandTotalValue={bandTotalValue} loadingMain={loadingMain} />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <KpiCard
          accent="blue"
          title="Total pipeline"
          sub={kpi ? `${kpi.totalCount} opportunities (won + lost + open)` : null}
        >
          {loadingMain && !kpi ? '…' : `${currency(kpi?.totalPipeline ?? 0)}`}
        </KpiCard>
        <KpiCard accent="green" title="Open pipeline" sub={kpi ? `${kpi.openCount} open` : null}>
          {loadingMain && !kpi ? '…' : `${currency(kpi?.openPipeline ?? 0)}`}
        </KpiCard>
        <KpiCard
          accent="amber"
          title="Win rate"
          sub={winRateKpi ? `${winRateKpi.wonCount} won / ${winRateKpi.lostCount} lost` : null}
        >
          {loadingMain && !winRateKpi ? '…' : `${(winRateKpi?.winRatePct ?? 0).toFixed(1)}%`}
        </KpiCard>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SalesFunnelBars
          rows={sortedStageRows}
          loading={loadingMain}
          onStagePick={handleChartStagePick}
          tooltipTheme={chartTooltipTheme}
        />
        <ExpectedCloseByMonthChart
          rows={closeMonthStacked}
          loading={loadingMain}
          tooltipTheme={chartTooltipTheme}
          onMonthProbBarClick={handleChartMonthProbBarClick}
          onProbLegendClick={handleChartProbLegendClick}
        />
        <SalespersonHorizontalChart
          rows={salespersonBars}
          loading={loadingMain}
          tooltipTheme={chartTooltipTheme}
          onSalespersonPick={handleChartSalespersonPick}
        />
        <CustomerTypeDonut
          rows={customerTypeDonut}
          loading={loadingMain}
          tooltipTheme={chartTooltipTheme}
          onTypePick={handleChartTypePick}
        />
      </div>

      {/* Opportunity table */}
      <div className="card-container rounded-md p-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
          <h2 className="text-lg font-semibold text-font-primary">
            Opportunity Details{' '}
            <span className="text-sm font-normal text-font-secondary">({filteredOpportunities.length} shown)</span>
          </h2>
          <div className="flex items-center gap-2 w-full sm:w-auto">
            <input
              type="search"
              placeholder="Search opp #, description, company, salesperson…"
              className="flex-1 sm:w-80 rounded-md px-3 py-2 text-sm border border-gray-200 dark:border-gray-600 bg-background text-font-primary"
              value={tableSearch}
              onChange={(e) => setTableSearch(e.target.value)}
            />
            <ExportOpportunitiesCSV data={filteredOpportunities} />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-font-secondary border-b border-gray-200 dark:border-gray-700">
                <th className="py-2 pr-3">Opp #</th>
                <th className="py-2 pr-3">Description</th>
                <th className="py-2 pr-3">Company</th>
                <th className="py-2 pr-3">Salesperson</th>
                <th className="py-2 pr-3">Status</th>
                <th className="py-2 pr-3">Stage</th>
                <th className="py-2 pr-3">Prob</th>
                <th className="py-2 pr-3 text-right">Value</th>
                <th className="py-2 pr-3">Close date</th>
                <th className="py-2 pr-3">Type</th>
              </tr>
            </thead>
            <tbody>
              {oppPagination.rows.map((row, idx) => (
                <tr
                  key={`${row.oppNumber}-${oppPagination.startIndex + idx}`}
                  className="border-b border-gray-100 dark:border-gray-800"
                >
                  <td className="py-2 pr-3">
                    {syteLineBase ? (
                      <button
                        type="button"
                        className="text-primary hover:underline font-mono text-left"
                        onClick={() => handleOppClick(row.oppNumber)}
                      >
                        {row.oppNumber || '—'}
                      </button>
                    ) : (
                      <span className="font-mono">{row.oppNumber || '—'}</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 max-w-[200px] truncate" title={row.description}>
                    {row.description}
                  </td>
                  <td className="py-2 pr-3">{row.company}</td>
                  <td className="py-2 pr-3">{row.salesperson}</td>
                  <td className="py-2 pr-3">{row.status}</td>
                  <td className="py-2 pr-3">{row.stage}</td>
                  <td className="py-2 pr-3">{row.probability}</td>
                  <td className="py-2 pr-3 text-right tabular-nums">{currency(row.value)}</td>
                  <td className="py-2 pr-3 whitespace-nowrap">{formatDisplayDate(row.closeDate) || row.closeDate}</td>
                  <td className="py-2 pr-3">{row.customerType}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {!loadingMain && filteredOpportunities.length === 0 && (
            <p className="text-sm text-font-secondary py-6 text-center">No opportunities match the current filters.</p>
          )}
        </div>

        <div className="flex-shrink-0 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 px-0 pt-3 mt-3 border-t border-gray-200 dark:border-gray-700 text-sm">
          <div className="hidden sm:block">
            <span className="text-font-secondary">
              Showing{' '}
              <span className="font-semibold text-primary">
                {oppPagination.total > 0 ? Math.min(oppPagination.startIndex + 1, oppPagination.total) : 0}
              </span>{' '}
              to{' '}
              <span className="font-semibold text-primary">
                {Math.min(oppPagination.startIndex + OPPORTUNITY_TABLE_PAGE_SIZE, oppPagination.total)}
              </span>{' '}
              of <span className="font-semibold text-primary">{oppPagination.total}</span> results
            </span>
          </div>
          <div className="flex items-center gap-1 sm:gap-2 px-0 sm:px-2 w-full sm:w-auto justify-between sm:justify-end">
            <button
              type="button"
              onClick={() => setOppTablePage(1)}
              disabled={oppTablePage === 1}
              className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
              aria-label="First page"
            >
              <Icon icon="icon-park-outline:double-left" className="text-lg sm:text-xl text-accent" />
            </button>
            <button
              type="button"
              onClick={() => setOppTablePage((prev) => Math.max(prev - 1, 1))}
              disabled={oppTablePage === 1}
              className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
              aria-label="Previous page"
            >
              <Icon icon="material-symbols:arrow-back-ios-new" className="text-base sm:text-lg text-accent" />
            </button>
            <span className="text-font-secondary text-xs sm:text-sm tabular-nums">
              <span className="font-bold text-accent">{oppTablePage}</span>/
              <span className="font-bold">{oppPagination.totalPages}</span>
            </span>
            <button
              type="button"
              onClick={() => setOppTablePage((prev) => Math.min(prev + 1, oppPagination.totalPages))}
              disabled={oppTablePage === oppPagination.totalPages}
              className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
              aria-label="Next page"
            >
              <Icon icon="material-symbols:arrow-forward-ios" className="text-base sm:text-lg text-accent" />
            </button>
            <button
              type="button"
              onClick={() => setOppTablePage(oppPagination.totalPages)}
              disabled={oppTablePage === oppPagination.totalPages}
              className="p-1 sm:p-2 disabled:opacity-50 disabled:cursor-not-allowed button-container"
              aria-label="Last page"
            >
              <Icon icon="icon-park-outline:double-right" className="text-lg sm:text-xl text-accent" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StateSealHome;
